import { LocationIcon } from '../../../components/ui/icons';
import { TagPill } from '../../sharing/TagPill';
import { surfaceShadows } from '../../../surfaceStyles';
import { displayKeywords } from '../../../domain/keywords';
import { useImageViewport } from '../../images/useImageViewport';
import { recordKey } from '../../collection/multiCollectionIdentity';
import { memo, useCallback, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Item } from '../../../domain/item';
import ImageWithStatus from '../../../components/ImageWithStatus';
import { CollectionSelectionController } from '../../collection/types';
import { useCollectionEntryInteraction } from '../../collection/hooks/useCollectionEntryInteraction';
import { ItemCaptureAttention } from './ItemCaptureAttention';

export const ItemGridCard = memo(function ItemGridCard({
  item,
  showLocation = true,
  showQuantity = true,
  selection,
  onOpen,
  itemPosition,
  totalItemCount,
  visibleIds,
}: {
  item: Item & { collectionName?: string };
  showLocation?: boolean;
  showQuantity?: boolean;
  selection: CollectionSelectionController;
  onOpen: (item: Item) => void;
  itemPosition?: number;
  totalItemCount?: number;
  visibleIds?: string[];
}) {
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const imageViewport = useImageViewport(item.requestImage);
  const openItem = useCallback(() => onOpen(item), [item, onOpen]);
  const interaction = useCollectionEntryInteraction({
    id: recordKey(item),
    label: displayKeywords(item.keywords),
    selection,
    onOpen: openItem,
    visibleIds,
    selectable: !item.localCapture,
  });
  const hasImage = Boolean(item.image) && failedImageUrl !== item.image;

  return (
    <Card
      data-testid={`item-card-${item.id}`}
      data-item-id={recordKey(item)}
      role="listitem"
      aria-setsize={totalItemCount}
      aria-posinset={itemPosition}
      sx={{
        minWidth: 0,
        height: '100%',
        position: 'relative',
        bgcolor: interaction.selected ? 'action.selected' : 'background.paper',
        border: 0,
        boxShadow: interaction.selected
          ? 'inset 0 0 0 1px currentColor'
          : surfaceShadows.raised,
        color: interaction.selected ? 'primary.main' : 'text.primary',
        borderRadius: 1,
        padding: 0,
        overflow: 'hidden',
        transform: interaction.pressing ? 'scale(.98)' : 'none',
        transition: 'transform 120ms',
      }}
    >
      <CardActionArea
        {...interaction.actionProps}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'stretch',
          touchAction: 'pan-y',
          alignItems: 'stretch',
        }}
      >
        <Box
          ref={imageViewport}
          data-testid="item-image-frame"
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            flexShrink: 0,
            bgcolor: 'background.default',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
          }}
        >
          {item.imageLoading ? (
            <CircularProgress size={24} aria-label="Loading image" />
          ) : hasImage ? (
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
              <ImageWithStatus
                src={item.image}
                alt={displayKeywords(item.keywords)}
                onBroken={() => setFailedImageUrl(item.image)}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  pointerEvents: 'none',
                }}
              />
            </Box>
          ) : (
            <Typography
              color="text.secondary"
              sx={{ px: 1, textAlign: 'center', fontSize: '0.875rem' }}
            >
              No image
            </Typography>
          )}
        </Box>
        <CardContent
          sx={{
            p: 1.25,
            width: '100%',
            minWidth: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            color: 'text.primary',
            '&:last-child': { pb: 1.25 },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 650,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {displayKeywords(item.keywords)}
          </Typography>
          {!!item.displayTags?.length && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 0.5,
                rowGap: 0.25,
                mt: 0.5,
              }}
            >
              {item.displayTags.map((tag) => (
                <TagPill key={tag.tagId} tag={tag} />
              ))}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.375,
              mt: 'auto',
              pt: 0.75,
              minWidth: 0,
              color: 'text.secondary',
            }}
          >
            {showLocation && (
              <LocationIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            )}
            {showLocation && (
              <Typography
                variant="caption"
                title={`${item.collectionName ? `${item.collectionName}/` : ''}${item.location || 'No location'}`}
                sx={{
                  lineHeight: 1.4,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {item.collectionName ? `${item.collectionName}/` : ''}
                {item.location || 'No location'}
              </Typography>
            )}
            {showQuantity && item.qty > 0 && (
              <Typography
                component="div"
                variant="caption"
                color="text.secondary"
                aria-label={`Quantity ${item.qty}`}
                sx={{
                  ml: 'auto',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                Qty {item.qty}
              </Typography>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
      {item.localCapture?.attention && (
        <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
          <ItemCaptureAttention item={item} />
        </Box>
      )}
      {selection.active && !item.localCapture && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 44,
            height: 44,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Checkbox
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
            checked={interaction.selected}
            inputProps={{
              'aria-label': `${interaction.selected ? 'Deselect' : 'Select'} ${displayKeywords(item.keywords)}`,
            }}
            onClick={(event) => {
              event.stopPropagation();
              interaction.toggle(event);
            }}
          />
        </Box>
      )}
    </Card>
  );
});
